import React, { useState, useEffect } from 'react';
import { Home, Save, Plus, Trash2, Award, Users, TrendingUp, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { homeContentAPI } from '../../services/api';
import { ImageUpload } from '../ui/image-upload';

const iconOptions = [
  { value: 'Award', label: 'Award', icon: Award },
  { value: 'Users', label: 'Users', icon: Users },
  { value: 'TrendingUp', label: 'TrendingUp', icon: TrendingUp },
  { value: 'Sparkles', label: 'Sparkles', icon: Sparkles },
];

const HomeTab = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await homeContentAPI.get();
      setContent(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei contenuti');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await homeContentAPI.update(content);
      toast.success('Contenuti salvati con successo!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const updateStat = (index, field, value) => {
    const newStats = [...content.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setContent({ ...content, stats: newStats });
  };

  const addStat = () => {
    const newStats = [...content.stats, { 
      icon: 'Award', 
      value: '', 
      label_it: '', 
      label_en: '' 
    }];
    setContent({ ...content, stats: newStats });
  };

  const removeStat = (index) => {
    const newStats = content.stats.filter((_, i) => i !== index);
    setContent({ ...content, stats: newStats });
  };

  const updateFeature = (index, field, value) => {
    const newFeatures = [...content.features];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    setContent({ ...content, features: newFeatures });
  };

  const addFeature = () => {
    const newFeatures = [...content.features, { 
      title_it: '', 
      title_en: '', 
      description_it: '', 
      description_en: '' 
    }];
    setContent({ ...content, features: newFeatures });
  };

  const removeFeature = (index) => {
    const newFeatures = content.features.filter((_, i) => i !== index);
    setContent({ ...content, features: newFeatures });
  };

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="py-8 text-center">Caricamento...</CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Home className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Gestione Home Page</CardTitle>
              <CardDescription>Modifica i contenuti della home page in doppia lingua</CardDescription>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-home-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Modifiche'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Hero Section */}
        <div className="space-y-4">
          <h3 className="font-display font-semibold text-lg">Sezione Hero</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Badge Hero (IT)</Label>
              <Input
                value={content.hero_badge_it || ''}
                onChange={(e) => setContent({ ...content, hero_badge_it: e.target.value })}
                className="mt-2"
                data-testid="hero-badge-it"
              />
            </div>
            <div>
              <Label>Badge Hero (EN)</Label>
              <Input
                value={content.hero_badge_en || ''}
                onChange={(e) => setContent({ ...content, hero_badge_en: e.target.value })}
                className="mt-2"
                data-testid="hero-badge-en"
              />
            </div>
          </div>
          <div>
            <Label>Immagine Hero Background</Label>
            <div className="mt-2">
              <ImageUpload
                value={content.hero_image || ''}
                onChange={(url) => setContent({ ...content, hero_image: url })}
                uploadType="image"
                label="Carica immagine hero"
              />
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-lg">Statistiche</h3>
            <Button variant="outline" size="sm" onClick={addStat} data-testid="add-stat-btn">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Statistica
            </Button>
          </div>
          <div className="space-y-4">
            {content.stats?.map((stat, index) => (
              <div key={index} className="p-4 border border-border rounded-lg space-y-4" data-testid={`stat-${index}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Statistica {index + 1}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeStat(index)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <Label>Icona</Label>
                    <Select value={stat.icon} onValueChange={(value) => updateStat(index, 'icon', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {iconOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center space-x-2">
                              <opt.icon className="w-4 h-4" />
                              <span>{opt.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Valore</Label>
                    <Input
                      value={stat.value}
                      onChange={(e) => updateStat(index, 'value', e.target.value)}
                      className="mt-2"
                      placeholder="25+"
                    />
                  </div>
                  <div>
                    <Label>Label (IT)</Label>
                    <Input
                      value={stat.label_it}
                      onChange={(e) => updateStat(index, 'label_it', e.target.value)}
                      className="mt-2"
                      placeholder="Anni di Esperienza"
                    />
                  </div>
                  <div>
                    <Label>Label (EN)</Label>
                    <Input
                      value={stat.label_en}
                      onChange={(e) => updateStat(index, 'label_en', e.target.value)}
                      className="mt-2"
                      placeholder="Years Experience"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-lg">Features</h3>
            <Button variant="outline" size="sm" onClick={addFeature} data-testid="add-feature-btn">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Feature
            </Button>
          </div>
          <div className="space-y-4">
            {content.features?.map((feature, index) => (
              <div key={index} className="p-4 border border-border rounded-lg space-y-4" data-testid={`feature-${index}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Feature {index + 1}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFeature(index)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Titolo (IT)</Label>
                    <Input
                      value={feature.title_it}
                      onChange={(e) => updateFeature(index, 'title_it', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Titolo (EN)</Label>
                    <Input
                      value={feature.title_en}
                      onChange={(e) => updateFeature(index, 'title_en', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Descrizione (IT)</Label>
                    <Textarea
                      value={feature.description_it}
                      onChange={(e) => updateFeature(index, 'description_it', e.target.value)}
                      className="mt-2 resize-none"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Descrizione (EN)</Label>
                    <Textarea
                      value={feature.description_en}
                      onChange={(e) => updateFeature(index, 'description_en', e.target.value)}
                      className="mt-2 resize-none"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HomeTab;
